import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowForward as ArrowIcon } from '@mui/icons-material';
import { BlogPost, getCategoryLabel, getCategoryColor } from '../../types/blog';

interface BlogCardProps {
  post: BlogPost;
}

export const BlogCard: React.FC<BlogCardProps> = ({ post }) => {
  const navigate = useNavigate();
  const categoryColor = getCategoryColor(post.category);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        border: 1,
        borderColor: 'divider',
        cursor: 'pointer',
        transition: 'all 0.2s',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          borderColor: categoryColor,
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
      onClick={() => navigate(`/blog/${post.slug}`)}
    >
      {/* Featured image */}
      {post.featuredImage && (
        <Box
          sx={{
            width: '100%',
            height: 180,
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={post.featuredImage}
            alt={post.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
      )}

      {/* Content */}
      <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Category chip */}
        <Box sx={{ mb: 2 }}>
          <Chip
            label={getCategoryLabel(post.category)}
            size="small"
            sx={{
              bgcolor: `${categoryColor}15`,
              color: categoryColor,
              fontWeight: 600,
            }}
          />
        </Box>

        {/* Title */}
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ lineHeight: 1.3 }}>
          {post.title}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.description}
        </Typography>

        {/* Meta info */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 'auto',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {formatDate(post.date)} · {post.readingTime} min read
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', color: categoryColor }}>
            <Typography variant="body2" fontWeight="600">
              Read
            </Typography>
            <ArrowIcon fontSize="small" sx={{ ml: 0.5 }} />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default BlogCard;
